import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import type { Sub, SubNotification } from '../types'
import { fmtDate, daysBetween } from '../lib/format'
import Badge, { type BadgeColor } from '../components/Badge'

function SubEditModal({
  sub,
  onClose,
  onSave,
}: {
  sub: Sub
  onClose: () => void
  onSave: (draft: Partial<Sub>) => Promise<void>
}) {
  const [draft, setDraft] = useState<Partial<Sub>>(() => ({
    contact_name: sub.contact_name,
    email: sub.email,
    phone: sub.phone,
    coi_expiration_date: sub.coi_expiration_date,
    w9_on_file: sub.w9_on_file,
    insurance_verified: sub.insurance_verified,
    notes: sub.notes,
    phone_e164: sub.phone_e164,
    prefers_text: sub.prefers_text,
    auto_send_notifications: sub.auto_send_notifications,
  }))
  const [saving, setSaving] = useState(false)
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof api.subs.get>> | null>(null)
  const [showRateForm, setShowRateForm] = useState(false)

  // Pull project history + cumulative ratings on open
  useEffect(() => {
    void api.subs.get(sub.id).then(setDetail)
  }, [sub.id])

  const ratings = detail?.ratings ?? []
  const projectsWorked = detail?.projects ?? []
  const ratedProjectIds = new Set(ratings.map((r) => r.project_id))
  const unratedProjects = projectsWorked.filter((p) => !ratedProjectIds.has(p.project_id))

  // Cumulative averages
  const avg = (key: 'quality_rating' | 'schedule_rating' | 'communication_rating' | 'cleanup_rating') => {
    const vals = ratings.map((r) => r[key]).filter((v): v is number => typeof v === 'number')
    if (vals.length === 0) return null
    return vals.reduce((s, v) => s + v, 0) / vals.length
  }
  const hireAgainCounts = {
    yes: ratings.filter((r) => r.would_hire_again === 'yes').length,
    conditional: ratings.filter((r) => r.would_hire_again === 'conditional').length,
    no: ratings.filter((r) => r.would_hire_again === 'no').length,
  }

  // Compute changed-fields count to surface "is dirty" to the user
  const dirty =
    (draft.contact_name ?? null) !== sub.contact_name ||
    (draft.email ?? null) !== sub.email ||
    (draft.phone ?? null) !== sub.phone ||
    (draft.coi_expiration_date ?? null) !== sub.coi_expiration_date ||
    draft.w9_on_file !== sub.w9_on_file ||
    draft.insurance_verified !== sub.insurance_verified ||
    (draft.notes ?? null) !== sub.notes ||
    (draft.phone_e164 ?? null) !== sub.phone_e164 ||
    draft.prefers_text !== sub.prefers_text ||
    draft.auto_send_notifications !== sub.auto_send_notifications

  function tryClose() {
    if (dirty && !window.confirm('Discard unsaved changes?')) return
    onClose()
  }

  async function save() {
    setSaving(true)
    try {
      await onSave({
        ...draft,
        contact_name: draft.contact_name || null,
        email: draft.email || null,
        phone: draft.phone || null,
        coi_expiration_date: draft.coi_expiration_date || null,
        notes: draft.notes || null,
        phone_e164: draft.phone_e164 || null,
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-bg" onClick={tryClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{sub.company_name}</h3>
        <p className="muted small mb-3">
          Edit fields below, then click <strong>Save changes</strong>. Nothing is saved until you do.
        </p>

        <label className="form-row">
          <span>Contact name</span>
          <input
            value={(draft.contact_name as string) ?? ''}
            onChange={(e) => setDraft({ ...draft, contact_name: e.target.value })}
          />
        </label>
        <label className="form-row">
          <span>Email</span>
          <input
            type="email"
            value={(draft.email as string) ?? ''}
            onChange={(e) => setDraft({ ...draft, email: e.target.value })}
          />
        </label>
        <label className="form-row">
          <span>Phone</span>
          <input
            value={(draft.phone as string) ?? ''}
            onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
          />
        </label>
        <label className="form-row">
          <span>COI expiration</span>
          <input
            type="date"
            value={(draft.coi_expiration_date as string) ?? ''}
            onChange={(e) => setDraft({ ...draft, coi_expiration_date: e.target.value })}
          />
        </label>
        <label className="form-row">
          <span>W-9 on file?</span>
          <input
            type="checkbox"
            checked={draft.w9_on_file === 1}
            onChange={(e) => setDraft({ ...draft, w9_on_file: e.target.checked ? 1 : 0 })}
          />
        </label>
        <label className="form-row">
          <span>Insurance verified?</span>
          <input
            type="checkbox"
            checked={draft.insurance_verified === 1}
            onChange={(e) => setDraft({ ...draft, insurance_verified: e.target.checked ? 1 : 0 })}
          />
        </label>
        <label className="form-row">
          <span>SMS phone (E.164)</span>
          <input
            value={(draft.phone_e164 as string) ?? ''}
            onChange={(e) => setDraft({ ...draft, phone_e164: e.target.value })}
            placeholder="+16518954845"
          />
        </label>
        <label className="form-row">
          <span>Prefers SMS over email?</span>
          <input
            type="checkbox"
            checked={draft.prefers_text === 1}
            onChange={(e) => setDraft({ ...draft, prefers_text: e.target.checked ? 1 : 0 })}
          />
        </label>
        <label className="form-row">
          <span>Auto-send notifications (skip review)?</span>
          <input
            type="checkbox"
            checked={draft.auto_send_notifications === 1}
            onChange={(e) =>
              setDraft({ ...draft, auto_send_notifications: e.target.checked ? 1 : 0 })
            }
          />
        </label>
        <label className="form-row">
          <span>Notes</span>
          <textarea
            rows={3}
            value={(draft.notes as string) ?? ''}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          />
        </label>

        <div className="row gap-2 mt-3">
          <button className="btn-primary" onClick={save} disabled={!dirty || saving}>
            {saving ? 'Saving…' : dirty ? 'Save changes' : 'No changes'}
          </button>
          <button className="btn-ghost" onClick={tryClose}>
            {dirty ? 'Discard' : 'Close'}
          </button>
        </div>

        {/* Performance section */}
        <div style={{ borderTop: '1px solid var(--border)', marginTop: 20, paddingTop: 16 }}>
          <h4 className="muted small" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Performance ({ratings.length} rating{ratings.length === 1 ? '' : 's'})
          </h4>

          {ratings.length > 0 && (
            <div className="card mb-2" style={{ background: 'var(--surface-2)' }}>
              <div className="row gap-3" style={{ flexWrap: 'wrap' }}>
                <Stat label="Quality" value={avg('quality_rating')} />
                <Stat label="Schedule" value={avg('schedule_rating')} />
                <Stat label="Comm." value={avg('communication_rating')} />
                <Stat label="Cleanup" value={avg('cleanup_rating')} />
                <div>
                  <div className="muted small">Hire again</div>
                  <div style={{ fontWeight: 600 }}>
                    {hireAgainCounts.yes}✓ {hireAgainCounts.conditional}? {hireAgainCounts.no}✕
                  </div>
                </div>
              </div>
            </div>
          )}

          {ratings.map((r) => (
            <div key={r.id} className="card mb-2" style={{ background: 'var(--surface-2)' }}>
              <div className="row-between">
                <div>
                  <strong>{r.project_name}</strong>
                  <div className="muted small">
                    Q{r.quality_rating ?? '—'} · S{r.schedule_rating ?? '—'} · C{r.communication_rating ?? '—'} · Cln{r.cleanup_rating ?? '—'} · hire-again: {r.would_hire_again ?? '—'}
                  </div>
                  {r.notes && <div className="small mt-1">{r.notes}</div>}
                </div>
                <div className="muted small">{r.rated_at.slice(0, 10)}</div>
              </div>
            </div>
          ))}

          {!showRateForm && unratedProjects.length > 0 && (
            <button className="btn-ghost small" onClick={() => setShowRateForm(true)}>
              + Rate this sub on a project ({unratedProjects.length} unrated)
            </button>
          )}
          {!showRateForm && unratedProjects.length === 0 && projectsWorked.length === 0 && (
            <p className="muted small">
              This sub isn't on any project yet. Add via the project's scope or sub_projects to enable rating.
            </p>
          )}
          {!showRateForm && unratedProjects.length === 0 && projectsWorked.length > 0 && (
            <p className="muted small">All projects rated.</p>
          )}

          {showRateForm && (
            <RateSubForm
              sub={sub}
              candidates={unratedProjects.map((p) => ({ project_id: p.project_id, project_name: p.project_name }))}
              onCancel={() => setShowRateForm(false)}
              onSaved={async () => {
                const refreshed = await api.subs.get(sub.id)
                setDetail(refreshed)
                setShowRateForm(false)
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: number | null }) {
  return (
    <div>
      <div className="muted small">{label}</div>
      <div style={{ fontWeight: 600 }}>{value === null ? '—' : value.toFixed(1)}</div>
    </div>
  )
}

function RateSubForm({
  sub,
  candidates,
  onCancel,
  onSaved,
}: {
  sub: Sub
  candidates: Array<{ project_id: string; project_name: string }>
  onCancel: () => void
  onSaved: () => Promise<void>
}) {
  const [projectId, setProjectId] = useState(candidates[0]?.project_id ?? '')
  const [q, setQ] = useState(4)
  const [s, setS] = useState(4)
  const [c, setC] = useState(4)
  const [cln, setCln] = useState(4)
  const [hire, setHire] = useState<'yes' | 'no' | 'conditional'>('yes')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)

  async function save() {
    if (!projectId) return
    setSaving(true)
    try {
      await api.subs.addPerformance({
        sub_id: sub.id,
        project_id: projectId,
        quality_rating: q,
        schedule_rating: s,
        communication_rating: c,
        cleanup_rating: cln,
        would_hire_again: hire,
        notes: notes || null,
      })
      await onSaved()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card mb-2">
      <h4 style={{ marginTop: 0 }}>Rate {sub.company_name}</h4>
      <label className="form-row">
        <span>Project</span>
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
          {candidates.map((p) => (
            <option key={p.project_id} value={p.project_id}>
              {p.project_name}
            </option>
          ))}
        </select>
      </label>
      {(['Quality', 'Schedule', 'Communication', 'Cleanup'] as const).map((label, idx) => {
        const val = [q, s, c, cln][idx]
        const setter = [setQ, setS, setC, setCln][idx]
        return (
          <label key={label} className="form-row">
            <span>{label} (1-5)</span>
            <select value={val} onChange={(e) => setter(parseInt(e.target.value))}>
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )
      })}
      <label className="form-row">
        <span>Hire again?</span>
        <select value={hire} onChange={(e) => setHire(e.target.value as 'yes' | 'no' | 'conditional')}>
          <option value="yes">yes</option>
          <option value="conditional">conditional</option>
          <option value="no">no</option>
        </select>
      </label>
      <label className="form-row">
        <span>Notes</span>
        <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>
      <div className="row gap-2 mt-2">
        <button className="btn-primary small" onClick={save} disabled={saving || !projectId}>
          {saving ? 'Saving…' : 'Save rating'}
        </button>
        <button className="btn-ghost small" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  )
}

function coiStatus(expiration: string | null): { label: string; color: BadgeColor } {
  if (!expiration) return { label: 'NO COI', color: 'gray' }
  const days = daysBetween(new Date(), expiration)
  if (days === null) return { label: 'NO COI', color: 'gray' }
  if (days < 0) return { label: `EXPIRED ${Math.abs(days)}d`, color: 'red' }
  if (days < 7) return { label: `${days}d LEFT`, color: 'red' }
  if (days < 30) return { label: `${days}d LEFT`, color: 'gold' }
  return { label: `${days}d`, color: 'green' }
}

export default function SubsTab() {
  const [subs, setSubs] = useState<Sub[]>([])
  const [editing, setEditing] = useState<Sub | null>(null)
  const [filter, setFilter] = useState('')
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState<Partial<Sub>>({})
  const [notifs, setNotifs] = useState<SubNotification[]>([])
  const [scanBusy, setScanBusy] = useState(false)
  const [notifMsg, setNotifMsg] = useState<string | null>(null)
  const [notifBusy, setNotifBusy] = useState<string | null>(null)

  useEffect(() => {
    api.subs.list().then(setSubs)
    void refreshNotifs()
  }, [])

  async function refreshNotifs() {
    try {
      const list = await api.subNotifications.list({ status: 'drafted' })
      setNotifs(list)
    } catch (e) {
      console.error('subNotifications', e)
    }
  }
  async function scanNotifs() {
    setScanBusy(true)
    setNotifMsg(null)
    try {
      const r = await api.subNotifications.scan()
      setNotifMsg(`Scan: ${r.candidates} candidates · ${r.drafted} drafted · ${r.auto_sent} auto-sent · ${r.failed} failed`)
      await refreshNotifs()
    } catch (e) {
      setNotifMsg(`Scan failed: ${(e as Error).message}`)
    } finally {
      setScanBusy(false)
    }
  }
  async function sendNotif(id: string) {
    setNotifBusy(id)
    try {
      await api.subNotifications.send(id)
      setNotifMsg('Notification sent.')
      await refreshNotifs()
    } catch (e) {
      setNotifMsg(`Send failed: ${(e as Error).message}`)
    } finally {
      setNotifBusy(null)
    }
  }
  async function dismissNotif(id: string) {
    if (!confirm('Dismiss this drafted notification?')) return
    await api.subNotifications.delete(id)
    await refreshNotifs()
  }

  async function add() {
    if (!draft.company_name) return
    const created = await api.subs.create({
      ...draft,
      trades: draft.trades
        ? JSON.stringify(
            String(draft.trades)
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          )
        : null,
    })
    setSubs((p) => [...p, created].sort((a, b) => a.company_name.localeCompare(b.company_name)))
    setAdding(false)
    setDraft({})
  }

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return subs
    return subs.filter(
      (s) =>
        s.company_name.toLowerCase().includes(q) ||
        (s.contact_name ?? '').toLowerCase().includes(q) ||
        (s.trades ?? '').toLowerCase().includes(q)
    )
  }, [subs, filter])

  async function persistAll(s: Sub, draft: Partial<Sub>) {
    const updated = await api.subs.update(s.id, draft)
    setSubs((p) => p.map((x) => (x.id === s.id ? updated : x)))
    setEditing(null)
  }

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Subcontractors</h2>
          <p className="muted small">
            {subs.length} subs · {subs.filter((s) => s.coi_expiration_date).length} with COI on file
          </p>
        </div>
        <div className="row gap-2">
          <input
            className="search"
            placeholder="Search company, trade…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <button className="btn-primary" onClick={() => setAdding(!adding)}>
            {adding ? 'Cancel' : '+ Sub'}
          </button>
        </div>
      </div>

      {/* v2 Module 5: pending sub notifications */}
      <div className="card mb-3" style={{ background: 'rgba(212,175,55,0.04)' }}>
        <div className="row-between">
          <div>
            <h3 style={{ margin: 0 }}>
              Pending notifications <span className="muted small">({notifs.length})</span>
            </h3>
            <p className="muted small">
              7-day-advance + 48-hour confirms drafted from the schedule. Approve to send via SMS
              (Twilio) or email (Gmail).
            </p>
          </div>
          <button className="btn-ghost" onClick={scanNotifs} disabled={scanBusy}>
            {scanBusy ? 'Scanning…' : '↻ Scan schedule now'}
          </button>
        </div>
        {notifMsg && <div className="muted small mt-2">{notifMsg}</div>}
        {notifs.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, marginTop: 8 }}>
            {notifs.map((n) => (
              <li key={n.id} className="card" style={{ marginBottom: 6, background: 'rgba(255,255,255,0.04)' }}>
                <div className="row-between">
                  <div>
                    <div className="row-title">
                      {n.sub_name ?? '(sub)'} ·{' '}
                      <span className="muted small">{n.kind} via {n.channel}</span>
                    </div>
                    <div className="muted small">
                      {n.project_name} {n.task_name && `· ${n.task_name}`} · to {n.recipient}
                    </div>
                    <div className="small mt-1">{n.body}</div>
                  </div>
                  <div className="row gap-2">
                    <button
                      className="btn-primary small"
                      onClick={() => sendNotif(n.id)}
                      disabled={notifBusy === n.id}
                    >
                      {notifBusy === n.id ? 'Sending…' : 'Approve + send'}
                    </button>
                    <button className="btn-ghost small" onClick={() => dismissNotif(n.id)}>
                      Dismiss
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {adding && (
        <div className="card mb-3">
          <label className="form-row">
            <span>Company name</span>
            <input
              value={draft.company_name ?? ''}
              onChange={(e) => setDraft({ ...draft, company_name: e.target.value })}
              placeholder="Acme Plumbing Inc"
            />
          </label>
          <label className="form-row">
            <span>Contact name</span>
            <input
              value={draft.contact_name ?? ''}
              onChange={(e) => setDraft({ ...draft, contact_name: e.target.value })}
            />
          </label>
          <label className="form-row">
            <span>Email</span>
            <input
              type="email"
              value={draft.email ?? ''}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            />
          </label>
          <label className="form-row">
            <span>Phone</span>
            <input
              value={draft.phone ?? ''}
              onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
            />
          </label>
          <label className="form-row">
            <span>Trades (comma-separated)</span>
            <input
              value={(draft.trades as string) ?? ''}
              onChange={(e) => setDraft({ ...draft, trades: e.target.value })}
              placeholder="plumbing, hvac"
            />
          </label>
          <label className="form-row">
            <span>COI expiration</span>
            <input
              type="date"
              value={draft.coi_expiration_date ?? ''}
              onChange={(e) => setDraft({ ...draft, coi_expiration_date: e.target.value })}
            />
          </label>
          <button className="btn-primary" onClick={add}>
            Add sub
          </button>
        </div>
      )}

      <div className="card no-pad">
        <div className="sub-grid sub-hdr">
          <div>Company</div>
          <div>Trades</div>
          <div>Contact</div>
          <div>COI</div>
          <div>W-9</div>
        </div>
        {filtered.map((s) => {
          const coi = coiStatus(s.coi_expiration_date)
          let trades: string[] = []
          try {
            trades = s.trades ? (JSON.parse(s.trades) as string[]) : []
          } catch {
            trades = []
          }
          return (
            <div key={s.id} className="sub-grid">
              <div className="bud-item">
                <button className="link" onClick={() => setEditing(s)}>
                  {s.company_name}
                </button>
              </div>
              <div className="muted small">{trades.join(', ')}</div>
              <div className="muted small">
                {s.contact_name ?? '—'}
                {s.email && (
                  <>
                    <br />
                    <a href={`mailto:${s.email}`}>{s.email}</a>
                  </>
                )}
              </div>
              <div>
                <Badge color={coi.color}>{coi.label}</Badge>
                {s.coi_expiration_date && (
                  <div className="muted small">{fmtDate(s.coi_expiration_date)}</div>
                )}
              </div>
              <div>{s.w9_on_file ? <Badge color="green">YES</Badge> : <Badge color="gray">NO</Badge>}</div>
            </div>
          )
        })}
      </div>

      {editing && (
        <SubEditModal
          sub={editing}
          onClose={() => setEditing(null)}
          onSave={(draft) => persistAll(editing, draft)}
        />
      )}
    </section>
  )
}
